// Parses an SVG containing a silhouette and splits pieces per the project's rules:
//  - fill close to #ff00ff  -> "fixed" (permanently black, non-editable detail)
//  - inside <g id="linked_..."> -> "linked" (multiple shapes, one shared photo)
//  - everything else        -> "editable" (a separate fillable slot)
//
// Supported shapes: <path>, <polygon>, and <polyline> - Illustrator can export
// some closed shapes as <polyline> instead of <polygon> (typically when the
// last anchor point wasn't explicitly connected back to the first while
// drawing), even though visually it's the same closed polygon. SVG's
// fill/clip-path rules implicitly close an open subpath at render time
// anyway, so a <polyline> can be treated identically to a <polygon>
// (shapemory-export.js already does this when converting to Path2D - it was
// only the parser that never collected these shapes before, so they didn't
// show up in the collage at all, not just as unfilled).
const SHAPE_SELECTOR = 'path, polygon, polyline';

function normalizeColor(str) {
  if (!str) return null;
  str = str.trim().toLowerCase();
  if (str === 'none' || str === 'transparent') return null;
  if (str.startsWith('#')) {
    let hex = str.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    if (hex.length !== 6) return null;
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  const m = str.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
    return [parts[0], parts[1], parts[2]];
  }
  return null;
}

// Tolerant magenta check - catches #ff00ff, #f0f, and slight export variance.
function isMagentaFill(fillStr) {
  const rgb = normalizeColor(fillStr);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  const tolerance = 20;
  return r >= 255 - tolerance && b >= 255 - tolerance && g <= tolerance;
}

function collectShapes(node) {
  return Array.from(node.querySelectorAll(SHAPE_SELECTOR));
}

// Recursively walks the tree of <g> elements. When it hits a
// <g id="linked_...">, it takes the whole group as one shared piece and
// doesn't descend into it further.
function walk(node, pieces, path) {
  for (const child of Array.from(node.children)) {
    const tag = child.tagName.toLowerCase();
    if (tag === 'g') {
      const id = child.getAttribute('id') || '';
      if (id.startsWith('linked_')) {
        const shapes = collectShapes(child);
        pieces.push({
          type: 'linked',
          id,
          shapes,
        });
      } else {
        walk(child, pieces, path.concat(id || 'g'));
      }
    } else if (tag === 'path' || tag === 'polygon' || tag === 'polyline') {
      const fill = child.getAttribute('fill');
      const parentId = path[path.length - 1] || 'piece';
      const siblingIndex = pieces.filter((p) => p.parentId === parentId).length;
      const autoId = siblingIndex === 0 ? parentId : `${parentId}_${siblingIndex}`;
      if (isMagentaFill(fill)) {
        pieces.push({ type: 'fixed', id: autoId, parentId, shapes: [child] });
      } else {
        pieces.push({ type: 'editable', id: autoId, parentId, shapes: [child] });
      }
    }
  }
}

// Returns { viewBox: [x,y,w,h], pieces: [...] } from the SVG file's text.
function parseShapemorySvg(svgText) {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const parseError = doc.querySelector('parsererror');
  if (parseError) throw new Error('Failed to parse SVG: ' + parseError.textContent);

  const svgRoot = doc.documentElement;
  const viewBox = (svgRoot.getAttribute('viewBox') || '0 0 100 100').split(/\s+/).map(Number);

  const pieces = [];
  walk(svgRoot, pieces, []);

  return { viewBox, pieces, svgRoot };
}
